import { produce } from 'immer';
import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  repeat,
  ImageAPI,
  classMap,
  nothing,
  ref,
  createRef,
} from 'omni-ui';
import { om2alert } from 'omni-campaign-ui';

import './campaign-image-carousel.js';
import { trackPortalEvent, bytesToMb } from '../helpers/utils.js';
import { MAX_IMAGE_BYTE_SIZE } from '../helpers/constants.js';

OmniStyleElement.register();

const MODULE_DEFAULT_DATA = {
  schema: {
    btnText: 'Attach',
    imgInputLabel: 'Upload Image',
    imgNameLabel: 'Image Name',
  },
  title: 'Attach Images',
};

/**
 * @summary Class for module Embed Images
 *  module that allows users to embed images into Campaigns and when the image thumbnail is clicked it opens the image in a carousel
 * @extends OmniElement
 */
export class CampaignAttachImages extends OmniElement {
  /**
   * @property {Array} attachedImages - array of objects containing the image url and title attached to the module
   * @property {Object} context - object containing module uuid and readOnly boolean
   * @property {Number} imageIndex - index of the selected image to be opened in carousel
   * @property {Array} uploadedImages - normalized image files returned from omni-img-input
   * @property {Boolean} isUploading - tracks when an image is being upload and returned by omni Image API
   * @property {Boolean} showCarousel -flag that keeps track if the carousel is open or not
   * @property {String} env: - environment application is currently in
   * @property {Object} overview - indicates overview mode, the value is the associated data
   */
  static get properties() {
    return {
      attachedImages: { type: Array },
      imageIndex: { type: Number },
      uploadedImages: { type: Array },
      isUploading: { type: Boolean },
      overview: { type: Object },
      showCarousel: { type: Boolean },
      context: { type: Boolean },
      moduleLocation: { type: String },
    };
  }

  /**
   * Embed images styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        omni-tile {
          margin-bottom: 2.14rem;
        }
        .omni h1.title {
          margin-left: 2.86rem;
        }
        omni-tile::part(body-slot) {
          padding: 2.14rem 2.86rem 4.29rem 0;
        }
        form.img-input-container {
          display: flex;
          justify-content: start;
          margin-bottom: 0.714rem;
        }
        omni-img-input::part(filename) {
          display: none;
        }
        .image-upload {
          margin-left: 2.86rem;
        }
        p.img-input-help {
          margin-top: -0.176rem;
          margin-left: 0.5rem;
        }
        .image-name {
          display: flex;
          flex-flow: column nowrap;
          justify-content: start;
          margin: 0 1.42rem;
          max-width: 21.5rem;
        }

        input.image-name-input {
          display: flex;
          flex-direction: column;
          height: 2.29rem;
          margin: 0 0 0.36rem 0;
        }
        .submit-btn {
          display: flex;
          margin-top: 1.56rem;
          justify-content: start;
          align-items: flex-start;
        }
        .submit-btn button.is-outlined {
          height: 2.29rem;
        }
        .thumbnail-container {
          display: flex;
          flex-flow: row wrap;
          overflow: auto;
        }
        .thumbnail-wrapper {
          width: 11.42rem;
          margin: 2.14rem 0 0 2.86rem;
          display: flex;
          flex-flow: column nowrap;
          align-items: center;
        }
        .thumbnail-image {
          width: 11.42rem;
          height: 11.42rem;
          border-radius: 0.714rem;
          border: solid thin rgb(var(--rgb-core-light));
        }
        .thumbnail-image:hover {
          box-shadow:
            0 1.71rem 2.21rem -0.43rem rgba(var(--rgb-nav-one), 0.37),
            0 0.14rem 0.64rem -0.07rem rgba(var(--rgb-nav-one), 0.1);
        }
        .action-btn-container {
          display: flex;
          justify-content: space-between;
          border-radius: 0.714rem;
        }
        .thumbnail-image:hover .action-btn-container {
          background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.2),
            rgba(0, 0, 0, 0)
          );
        }
        button.action-btn {
          height: 1.29rem;
          border: none;
          padding: 0;
          margin: 0.82rem 0.82rem;
          cursor: pointer;
          display: none;
        }
        .action-btn omni-icon::part(icon) {
          --color-icon-lines: rgb(var(--rgb-core-light));
          fill: rgb(var(--rgb-core-light));
        }
        .thumbnail-image:hover button.action-btn {
          display: flex;
          justify-content: center;
          background: transparent;
          stroke: white;
        }
        .omni .thumbnail-title {
          height: 1.51rem;
          margin-top: 1rem;
          opacity: 0.9;
          color: rgb(var(--rgb-core-dark));
          background: transparent;
          border: none;
        }
        .omni .thumbnail-title:hover {
          border-radius: 0.43rem;
          border: thin solid rgb(var(--rgb-border));
          background-color: rgb(242, 245, 250);
        }
        .thumbnail-title:focus-within {
          border: solid thin rgb(var(--rgb-primary));
        }
      `,
    ];
  }

  /**
   * Initialize {@link #ANsid} {@link #imageIndex}, {@link #showCarousel}, {@link #uploadedImages}, {@link #isUploading}, {@link #attachedImages}, {@link #imgInputRef}, and {@link #attachBtnRef}.
   */
  constructor() {
    super();
    this.ANsid = window?.AN?.sso?.session?.sid;
    this.imageIndex = 0;
    this.showCarousel = false;
    this.uploadedImages = [];
    this.attachedImages = [];
    this.isUploading = false;
    this.imgInputRef = createRef();
    this.attachBtnRef = createRef();
  }

  /**
   * Spreads any prior saved images into {@link #attachedImages}
   */
  connectedCallback() {
    super.connectedCallback();
    this.attachedImages = this.overview
      ? this.overview.map(module => module.saved_data?.images).flat()
      : [...(this.savedData?.images ?? [])];
    this.moduleTitle ||= MODULE_DEFAULT_DATA.title;
    this.schema ||= MODULE_DEFAULT_DATA.schema;
    this.imageAPI = new ImageAPI(this.ANsid, this.env);
    this.moduleLocation =
      new URL(window.location.href).searchParams.get('view') === 'Assets'
        ? 'assets page'
        : 'attach images module';
  }

  /**
   * Returns an array of row data to be included in csv export
   */
  get csvData() {
    return this.attachedImages.map(x => ({
      type: 'image',
      name: x.title,
      url: x.url,
    }));
  }

  /**
   * Notifies the app what module has new data to save and how to save it.
   */
  _stateChange() {
    const { uuid_module: moduleId } = this.context;
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: this._submitImages.bind(this),
      },
    });
  }

  /**
   * Function that gets passed down from campaign-step-detail that saves new data to campaigns API
   */
  async _submitImages() {
    await this.save({ images: this.attachedImages });
  }

  /**
   * Uploads images added to `omni-img-input` to the image API on form submit to save image URLs to the state, and resets the form
   * @param {SubmitEvent} e - the object from the form wrapping attach images
   * @type {HTMLFormElement}
   * @listens HTMLFormElement#submit
   */
  _attachImages = async e => {
    e.preventDefault();
    try {
      this.isUploading = true;
      if (!this.ANsid) throw new Error("Missing ANsid: can't upload images");
      const uploadedImages = await Promise.all(
        this.uploadedImages.map(async ({ file, title }) => {
          const url = await this.imageAPI.createImageURL(file);
          return { title, url };
        }),
      );
      this.attachedImages = produce(this.attachedImages, draft =>
        draft.concat(uploadedImages),
      );
      trackPortalEvent('image', 'attach', this.moduleLocation);
      this.isUploading = false;
      this.uploadedImages = [];
      this.imgInputRef.value.reset();
      this.attachBtnRef.value.blur();
      this._stateChange();
    } catch (e2) {
      if (e2.message) {
        om2alert(e2.message);
      } else {
        try {
          const error = await e2.json();
          om2alert(error.message);
        } catch {
          om2alert('Image upload failed');
        }
      }

      this.isUploading = false;
    }
  };

  /**
   * Normalizes the files passed by omni-img-input to allow users to edit the image name before hitting the Image API
   * @param {File[]} files image files from omni-img-input
   */
  _uploadedImages = files => {
    const prevUploadedImages = this.uploadedImages.map(obj => obj.file.name);
    const newUploadedImages = files.map(file => file.name);
    let uploadedImages = [];
    // When files have been removed, we keep the normalized objects from this.uploadedImages
    if (files.length < prevUploadedImages.length) {
      uploadedImages = this.uploadedImages.filter(imgObject =>
        newUploadedImages.includes(imgObject.file.name),
      );
      this.uploadedImages = [...uploadedImages];
    } else {
      // When files have been added, we filter out the new files from the passed in array and normalize the files
      uploadedImages = files.filter(
        file => !prevUploadedImages.includes(file.name),
      );
      const formattedImages = uploadedImages.map(file => ({
        file,
        title: file.name.substring(0, file.name.lastIndexOf('.')),
        url: '',
      }));
      this.uploadedImages = [...this.uploadedImages, ...formattedImages];
    }
  };

  /**
   * Removes desired imaged from array {@link #attachedImages} and follows immutable principles
   * @param {Number} index - index of the image that needs to be removed
   */
  _removeImage(index) {
    trackPortalEvent('image', 'delete', this.moduleLocation);
    this.attachedImages = produce(this.attachedImages, draft => {
      draft.splice(index, 1);
    });
    this._stateChange();
  }

  /**
   * Downloads image locally. Image object is passed down to it by {@link #_renderThumbnail}
   * @param {String} url -  url pointing to the image, destructured from image
   * @param {String} title - name of image, destructured from image
   * @todo This should probably be a util or just live outside the class
   */
  _downloadImage = ({ url, title }) => {
    trackPortalEvent('image', 'export', this.moduleLocation);
    fetch(url)
      .then(resp => resp?.blob())
      .then(blob => {
        const a = document.createElement('a');
        const fileURL = URL.createObjectURL(blob);
        a.href = fileURL;
        a.download = `${title}${url.substring(url.lastIndexOf('.'))}`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(fileURL);
        a.remove();
      });
  };

  /**
   * Function toggles the carousel and it gets passed down to campaign-image-carousel and also provides it the index of the image the carousel needs to open on
   * @param {Number} imageIndex - index of image that carousel needs to be open on
   */
  _toggleCarousel = imageIndex => {
    trackPortalEvent('image', 'open', this.moduleLocation);
    this.imageIndex = imageIndex;
    this.showCarousel = !this.showCarousel;
  };

  /**
   * @returns {html} of input fields that allows users to edit the name of their image
   */
  _renderImgTitleInput() {
    return html`
      <div class="image-name">
        ${this.uploadedImages?.length
          ? html`<label class="label is-size-7"
              >${this.schema.imgNameLabel}</label
            >`
          : nothing}
        ${repeat(
          this.uploadedImages,
          ({ url }) => url,
          (image, index) => html`
            <input
              class="input image-name-input"
              type="text"
              .value=${image.title}
              placeholder="Name your file"
              @input=${e => {
                const { value } = e.target;
                this.uploadedImages = produce(this.uploadedImages, draft => {
                  draft[index].title = value;
                });
              }}
            />
          `,
        )}
      </div>
    `;
  }

  /**
   * @returns {html} for the images individual thumbnail that gets embed into the module.
   */
  _renderThumbnail() {
    // escape single quotes so that images url doesn't break.
    const imageURL = image => image.url?.replace(/'/g, '%27');
    return html`
      <div class="thumbnail-container">
        ${repeat(
          this.attachedImages,
          ({ url }) => url,
          (image, index) => html`
            <div class="thumbnail-wrapper">
              <div
                class="thumbnail-image"
                style="background:url('${imageURL(
                  image,
                )}');background-size: cover;"
                @click=${() => this._toggleCarousel(index)}
              >
                <div class="action-btn-container">
                  <omni-tooltip slot="end" class="download-position">
                    <button
                      slot="invoker"
                      class="action-btn"
                      @click=${e => {
                        e.stopPropagation();
                        this._downloadImage(image);
                      }}
                    >
                      <omni-icon
                        class="is-size-2"
                        icon-id="omni:interactive:download"
                      ></omni-icon>
                    </button>
                    <div slot="content">Download</div>
                  </omni-tooltip>
                  <omni-tooltip
                    slot="end"
                    class="remove-position ${this.overview ||
                    this.context.readonly
                      ? 'is-hidden'
                      : ''}"
                  >
                    <button
                      slot="invoker"
                      class="action-btn"
                      @click=${e => {
                        e.stopPropagation();
                        this._removeImage(index);
                      }}
                      ?disabled=${this.context.readonly}
                    >
                      <omni-icon
                        class="is-size-2"
                        icon-id="omni:interactive:remove"
                      ></omni-icon>
                    </button>
                    <div slot="content">remove</div>
                  </omni-tooltip>
                </div>
              </div>
              <input
                class="thumbnail-title is-clamped clamp-2 has-text-centered"
                type="text"
                .value=${image.title}
                placeholder="Name your image"
                @change=${e => {
                  const { value } = e.target;
                  this.attachedImages = produce(this.attachedImages, draft => {
                    draft[index].title = value;
                  });
                  trackPortalEvent('image name', 'edit', this.moduleLocation);
                  this._stateChange();
                }}
                ?disabled=${this.context.readonly}
              />
            </div>
          `,
        )}
      </div>
    `;
  }

  /**
   * @returns {html} for the module CampaignAttachImages.
   */
  render() {
    const hideIfOverview = this.overview ? 'is-hidden' : '';
    if (this.overview && this.attachedImages.length === 0)
      return html`<slot name="empty-overview"></slot>`;

    return html` <omni-style>
      <omni-tile>
        <h1 class="title mb-5 is-3 ${hideIfOverview}">${this.moduleTitle}</h1>
        <form
          @submit=${this._attachImages}
          class=" img-input-container ${hideIfOverview}"
        >
          <div class="image-upload">
            <div class="is-flex is-flex-direction-column is-flex-wrap-nowrap">
              <label class="is-size-7"> ${this.schema.imgInputLabel}</label>
              <omni-img-input
                ${ref(this.imgInputRef)}
                multiple
                previewable
                maxSize=${MAX_IMAGE_BYTE_SIZE}
                @change=${e => {
                  const { files } = e.detail;
                  this._uploadedImages(files);
                }}
                .env=${this.env}
                ?disabled=${this.context.readonly}
              >
                <p class="img-input-help" slot="help">
                  Max size ${bytesToMb(MAX_IMAGE_BYTE_SIZE)}MB
                </p>
              </omni-img-input>
            </div>
          </div>
          ${this._renderImgTitleInput()}
          <div class="submit-btn">
            <button
              ${ref(this.attachBtnRef)}
              type="submit"
              class=${classMap({
                button: true,
                'is-outlined': true,
                'is-loading': this.isUploading,
              })}
              ?disabled=${this.context.readonly ||
              this.uploadedImages.length === 0}
            >
              <span class="icon">
                <omni-icon class="is-size-1" icon-id="omni:informative:check">
                </omni-icon>
              </span>
              <span>${this.schema.btnText}</span>
            </button>
          </div>
        </form>
        ${this._renderThumbnail()}
      </omni-tile>
      <campaign-image-carousel
        .handleToggle=${this._toggleCarousel}
        .showCarousel=${this.showCarousel}
        .images=${this.attachedImages}
        .imageIndex=${this.imageIndex}
      >
      </campaign-image-carousel>
    </omni-style>`;
  }
}

OmniElement.register('campaign-attach-images', CampaignAttachImages);
